import React, { useContext, useMemo } from "react";
import { Link } from "react-router-dom";

import cx from "classnames";
import { Node as ProseNode } from "prosemirror-model";

import { Views } from "~/actionItems/constants";
import { ActionItemsContext } from "~/actionItems/newActionItems/ActionItemsContext";
import type { ActionItem } from "~/actionItems/types";
import { encodeNoteUrl } from "~/actionItems/utils";
import { ActionItemLink } from "~/common/ActionItemLinkageModal";
import BlockTextEditor, { ChangeAction } from "~/lib/prose/BlockTextEditor";
import LinkedItemText from "~/notes/LinkedItemText";

import styles from "./ActionItemText.module.css";

type ActionItemTextProps = {
	item: ActionItem;
	transforms: ((node: ProseNode) => ProseNode)[];
	editing: boolean;
	readOnly?: boolean;
	onContentChange?: (prevDoc: ProseNode, doc: ProseNode, action: ChangeAction) => void;
	inMultiDragging?: boolean;
	className?: string;
};

function isActionItemEntityRange(value: unknown): value is {
	key: string;
	length: number;
	offset: number;
} {
	return (
		typeof value === "object" &&
		value !== null &&
		Object.prototype.hasOwnProperty.call(value, "key") &&
		"key" in value &&
		typeof (value as { key: unknown }).key === "string" &&
		"length" in value &&
		typeof (value as { length: unknown }).length === "number" &&
		"offset" in value &&
		typeof (value as { offset: unknown }).offset === "number"
	);
}

function isActionItemEntityRangeArray(value: unknown): value is ReadonlyArray<{
	key: string;
	length: number;
	offset: number;
}> {
	return Array.isArray(value) && value.every(item => isActionItemEntityRange(item));
}

export default function ActionItemText({
	item,
	transforms,
	editing,
	readOnly,
	onContentChange,
	inMultiDragging,
	className,
}: ActionItemTextProps) {
	const { id, text, note, wontDo, hasOriginalBlock, originalBlock, hasLink, hasAccessToNote } = item;
	const { view } = useContext(ActionItemsContext);

	const entityRanges = React.useMemo(
		() => (isActionItemEntityRangeArray(item.entityRanges) ? item.entityRanges : []),
		[item.entityRanges],
	);

	const participants = useMemo(
		() =>
			note
				? note.noteuserSet
						.map(({ user }) => user)
						.filter(user => !("invitable" in user) || (!user.internalAttributes?.invitable && user !== null))
				: [],
		[note],
	);

	const linkLocation = useMemo(() => {
		if (!note) return null;
		const hash = encodeNoteUrl({
			noteId: note.id,
			blockKey: item.key,
		});
		if (view === Views.STREAM_TAB) {
			return {
				pathname: "../notes/",
				hash,
			};
		}
		if (!note.stream?.url || note.stream.isTodoStream) return;
		return {
			pathname: `${note.stream.url}notes/`,
			hash,
		};
	}, [note, item.key, view]);

	const initialBlockAttrs = useMemo(() => {
		if (!item.recordingId || !item.recordingOffset) return undefined;
		return {
			recording: item.recordingId,
			recordingOffset: item.recordingOffset,
		};
	}, [item.recordingId, item.recordingOffset]);

	const readonlyEditor = useMemo(
		() =>
			editing && !readOnly ? null : (
				<BlockTextEditor
					className={className}
					key={`${id}-${editing}`}
					text={text}
					entityMap={note?.entityMap || {}}
					entityRanges={entityRanges || []}
					enabled={false}
					transforms={transforms}
					participants={participants}
					initialBlockAttrs={initialBlockAttrs}
				/>
			),
		[
			editing,
			readOnly,
			className,
			id,
			text,
			note?.entityMap,
			entityRanges,
			transforms,
			participants,
			initialBlockAttrs,
		],
	);

	return (
		<div className={cx(styles.actionItemText, { [styles.inMultiDragging]: inMultiDragging })}>
			{hasLink && hasAccessToNote && <ActionItemLink blockId={id} isOriginal={!hasOriginalBlock} />}
			<div className={cx({ [styles.wontDoText]: wontDo }, styles.proseWrapper)}>
				{editing && !readOnly ? (
					<BlockTextEditor
						deleteOut
						key={`${id}-${editing}`}
						text={text}
						entityMap={note.entityMap}
						entityRanges={entityRanges || []}
						participants={participants}
						enabled={editing}
						onSubmit={onContentChange}
						className={cx(className, { [styles.editing]: editing })}
						hideControls
						initialBlockAttrs={initialBlockAttrs}
					/>
				) : hasOriginalBlock ? (
					<LinkedItemText
						originalBlockId={originalBlock?.id ?? undefined}
						originatingNoteId={note?.id}
						hasAccessToOriginalNote={!!originalBlock}
					>
						{readonlyEditor}
					</LinkedItemText>
				) : view !== Views.STREAM_TAB || !linkLocation ? (
					readonlyEditor
				) : (
					<Link to={linkLocation} className={styles.link}>
						{readonlyEditor}
					</Link>
				)}
			</div>
		</div>
	);
}
